/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.client

import io.ktor.client.HttpClient
import io.ktor.client.HttpClientConfig
import io.ktor.client.call.body
import io.ktor.client.plugins.ClientRequestException
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.defaultRequest
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.forms.submitForm
import io.ktor.client.request.headers
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.request.url
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.headersOf
import io.ktor.http.isSuccess
import io.ktor.http.parametersOf
import io.ktor.http.path
import io.ktor.serialization.kotlinx.json.json
import systems.frictionless.impulse.rest.client.internal.BasicAuthHeader
import systems.frictionless.impulse.rest.client.internal.BearerAuthHeader
import systems.frictionless.impulse.rest.client.internal.ResponseWithHeaders
import systems.frictionless.impulse.rest.client.internal.getProgress
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.client.model.QuestionWithProgress
import systems.frictionless.impulse.rest.client.model.RestResponse
import systems.frictionless.impulse.rest.internal.SerializedEmail
import systems.frictionless.impulse.rest.model.Answer
import systems.frictionless.impulse.rest.model.Assessment
import systems.frictionless.impulse.rest.model.CommonErrorCodes
import systems.frictionless.impulse.rest.model.Email
import systems.frictionless.impulse.rest.model.Insight
import systems.frictionless.impulse.rest.model.Login
import systems.frictionless.impulse.rest.model.Password
import systems.frictionless.impulse.rest.model.Question
import systems.frictionless.impulse.rest.model.ServerError
import systems.frictionless.impulse.rest.model.Session
import systems.frictionless.impulse.rest.model.User
import systems.frictionless.impulse.rest.route.AnswerPath
import systems.frictionless.impulse.rest.route.AssessmentPath
import systems.frictionless.impulse.rest.route.HeartbeatPath
import systems.frictionless.impulse.rest.route.LoginPath
import systems.frictionless.impulse.rest.route.SessionForm
import systems.frictionless.impulse.rest.route.SessionPath
import systems.frictionless.impulse.rest.route.UserPath

/*
 * The implementation has one additional layer of indirection, because the response headers also influence
 * the returned object.
 */

/**
 * @param httpClient Client to communicate with the server.  Typically, use [newHttpClient] although custom clients
 * can be injected for testing.  To prevent leaks, the client must be closed.  DefaultRestClient does not take ownership of the httpClient.
 */
@Suppress("TooManyFunctions")
class DefaultRestClient(private val httpClient: HttpClient) : RestClient {
    override suspend fun check(): RestResponse<Unit> =
        httpClient.requestHelper<Unit, Unit>(HttpMethod.Get, Authentication.None, HeartbeatPath.ROOT, null).response

    override suspend fun login(loginBuilder: Login.Builder): RestResponse<Login> =
        httpClient.requestHelper<Login.Builder, Login>(HttpMethod.Post, Authentication.None, LoginPath.ROOT, loginBuilder).response

    override suspend fun getUser(authentication: Authentication) =
        httpClient.requestHelper<Unit, User>(HttpMethod.Get, authentication, UserPath.ROOT, null).response

    override suspend fun createUser(userBuilder: User.Builder): RestResponse<User> =
        httpClient.requestHelper<User.Builder, User>(HttpMethod.Post, Authentication.None, UserPath.ROOT, userBuilder).response

    override suspend fun deleteUser(authentication: Authentication): RestResponse<Unit> =
        httpClient.requestHelper<Authentication, Unit>(
            HttpMethod.Delete,
            authentication,
            UserPath.ROOT,
            null
        ).response

    override suspend fun changePassword(
        authentication: Authentication,
        password: Password
    ): RestResponse<Unit> =
        httpClient.requestHelper<Password, Unit>(
            HttpMethod.Post,
            authentication,
            UserPath.PASSWORD,
            password
        ).response

    override suspend fun forgotPassword(email: Email): RestResponse<Unit> =
        httpClient.requestHelper<SerializedEmail, Unit>(
            HttpMethod.Post,
            Authentication.None,
            UserPath.FORGOT_PASSWORD,
            SerializedEmail(email)
        ).response

    override suspend fun createSession(authentication: Authentication.Basic): RestResponse<Authentication.Bearer> {
        // Note: uses post because it causes state transformation on the server
        return httpClient.requestHelper<Unit, Session>(
            HttpMethod.Post,
            authentication,
            SessionPath.ROOT,
            null
        ).response.remap {
            RestResponse.Success(Authentication.Bearer(it.result.token))
        }
    }

    override suspend fun createSession(authentication: Authentication.Form): RestResponse<Authentication.Bearer> =
        requestSafely<Session> {
            httpClient.submitForm(
                formParameters =
                    parametersOf(
                        SessionForm.KEY_USERNAME to listOf(authentication.email.emailString),
                        SessionForm.KEY_PASSWORD to listOf(authentication.password)
                    )
            ) {
                url(path = SessionPath.ROOT)
            }
        }.response.remap {
            RestResponse.Success(Authentication.Bearer(it.result.token))
        }

    override suspend fun getAssessments(authentication: Authentication): RestResponse<List<Assessment>> =
        httpClient.requestHelper<Unit, List<Assessment>>(
            HttpMethod.Get,
            authentication,
            AssessmentPath.ROOT,
            null
        ).response

    override suspend fun getAssessmentStatus(
        authentication: Authentication,
        assessment: Assessment
    ): RestResponse<Assessment.State> =
        httpClient.requestHelper<Unit, Assessment.State>(
            HttpMethod.Get,
            authentication,
            AssessmentPath.withIdAndState(assessment.id),
            null
        ).response

    override suspend fun createAssessment(
        authentication: Authentication,
        assessmentBuilder: Assessment.Builder
    ): RestResponse<Assessment> =
        httpClient.requestHelper<Assessment.Builder, Assessment>(
            HttpMethod.Post,
            authentication,
            AssessmentPath.ROOT,
            assessmentBuilder
        ).response

    override suspend fun createAnswer(
        authentication: Authentication,
        answerBuilder: Answer.Builder
    ): RestResponse<Answer> =
        httpClient.requestHelper<Answer.Builder, Answer>(HttpMethod.Post, authentication, AnswerPath.ROOT, answerBuilder).response

    override suspend fun getQuestion(path: String): RestResponse<Question> =
        httpClient.requestHelper<Unit, Question>(HttpMethod.Get, Authentication.None, path, null).response

    override suspend fun getNextQuestion(
        authentication: Authentication,
        assessment: Assessment
    ): RestResponse<QuestionWithProgress?> =
        httpClient.getHelperNullable<Question?>(
            authentication,
            AssessmentPath.withIdAndNextQuestion(assessment.id)
        ).let {
            it.remap { success, headers ->
                success.result?.let { question ->
                    RestResponse.Success(
                        QuestionWithProgress(
                            question,
                            headers.getProgress()
                        )
                    )
                } ?: RestResponse.Success(null)
            }
        }

    override suspend fun getInsights(
        authentication: Authentication,
        assessment: Assessment
    ): RestResponse<Insight> =
        httpClient.requestHelper<Unit, Insight>(
            HttpMethod.Get,
            authentication,
            AssessmentPath.withIdAndInsight(assessment.id),
            null
        ).response

    companion object {
        /*
         * `internal` to allow unit tests to verify this is off before deployment.  Logs will contain
         * login credentials in the headers, so this shouldn't be printed for production.
         */
        internal const val IS_VERBOSE_LOGGING = false

        fun newHttpClient(defaultUrl: String?) =
            HttpClient {
                defaultUrl?.let {
                    defaultRequest {
                        url(defaultUrl)
                    }
                }

                configureJson()

                if (IS_VERBOSE_LOGGING) {
                    install(Logging) {
                        level = LogLevel.ALL
                        sanitizeHeader { it == HttpHeaders.Authorization }
                    }
                }
            }
    }
}

fun HttpClientConfig<*>.configureJson() {
    install(ContentNegotiation) {
        json(
            json =
                kotlinx.serialization.json.Json {
                /*
                 * Disabling array polymorphism is required for consistent serialization/deserialization, especially for
                 * Question and Answer objects
                 */
                    useArrayPolymorphism = false
                }
        )
    }
}

/**
 * @param httpMethod Method for the request.
 * @param obj Object to include in the request body.  Object must support JSON serialization through the kotlinx serialization library.  May be null, indicating no
 * body to the request.
 * @param path Url path to perform request on.
 */
private suspend inline fun <reified Request, reified Response> HttpClient.requestHelper(
    httpMethod: HttpMethod,
    authentication: Authentication,
    path: String,
    obj: Request?
): ResponseWithHeaders<Response> {
    return requestSafely {
        request(path) {
            url {
                path(path)
            }

            method = httpMethod

            configureAuth(authentication)

            obj?.let {
                contentType(ContentType.Application.Json)
                setBody(obj)
            }
        }
    }
}

/*
 * Generic exceptions required for multiplatform, unless we typealias for each platform.
 *
 * Lots of returns are necessary for different paths, for which there's not a good solution.
 */
@Suppress("TooGenericExceptionCaught", "ReturnCount")
private suspend inline fun <reified T> requestSafely(requester: () -> HttpResponse): ResponseWithHeaders<T> {
    val tryRequest =
        runCatching { requester() }
            .onFailure { exception ->
                if (exception is ClientRequestException) {
                    val serverError: ServerError =
                        try {
                            exception.response.body()
                        } catch (e: Throwable) {
                            if (exception.response.status == HttpStatusCode.Unauthorized) {
                                ServerError(
                                    CommonErrorCodes.UNAUTHORIZED,
                                    exception.message
                                )
                            } else {
                                ServerError(
                                    CommonErrorCodes.PROXY,
                                    "Failed decoding error from server; original message is ${e.message}"
                                )
                            }
                        }

                    return ResponseWithHeaders(
                        RestResponse.Failure.Server(exception.response.status, serverError),
                        exception.response.headers
                    )
                } else {
                /* The connection failures will be different exception types on different platforms
                 * JVM will be java.net.ConnectException
                 * Javascript will be something else.
                 * Since this code is multiplatform, it is hard to rely on platform-specific exception types.
                 */
                    return ResponseWithHeaders(RestResponse.Failure.Connection(exception), headersOf())
                }
            }

    val response = tryRequest.getOrThrow()

    if (!response.status.isSuccess()) {
        return ResponseWithHeaders(RestResponse.Failure.Server(response.status, response.toServerError()), response.headers)
    }

    return try {
        ResponseWithHeaders(RestResponse.Success(response.body()), response.headers)
    } catch (e: Throwable) {
        // There might be some corner cases that aren't entirely client failures
        ResponseWithHeaders(RestResponse.Failure.Client(e), response.headers)
    }
}

/*
 * Generic exceptions required for multiplatform, unless we typealias for each platform.
 *
 * Lots of returns are necessary for different paths, for which there's not a good solution.
 */
@Suppress("TooGenericExceptionCaught", "ReturnCount")
private suspend inline fun <reified T> requestSafelyNullable(requester: () -> HttpResponse): ResponseWithHeaders<T?> {
    val tryRequest =
        runCatching { requester() }
            .onFailure { exception ->
                if (exception is ClientRequestException) {
                    val serverError: ServerError =
                        try {
                            exception.response.body()
                        } catch (e: Throwable) {
                            if (exception.response.status == HttpStatusCode.Unauthorized) {
                                ServerError(
                                    CommonErrorCodes.UNAUTHORIZED,
                                    exception.message
                                )
                            } else {
                                ServerError(
                                    CommonErrorCodes.PROXY,
                                    "Failed decoding error from server; original message is ${e.message}"
                                )
                            }
                        }

                    return ResponseWithHeaders(
                        RestResponse.Failure.Server(exception.response.status, serverError),
                        exception.response.headers
                    )
                } else {
                /* The connection failures will be different exception types on different platforms
                 * JVM will be java.net.ConnectException
                 * Javascript will be something else.
                 * Since this code is multiplatform, it is hard to rely on platform-specific exception types.
                 */
                    return ResponseWithHeaders(RestResponse.Failure.Connection(exception), headersOf())
                }
            }

    val response = tryRequest.getOrThrow()

    if (!response.status.isSuccess()) {
        return ResponseWithHeaders(RestResponse.Failure.Server(response.status, response.toServerError()), response.headers)
    }

    return try {
        if (HttpStatusCode.NoContent == response.status) {
            ResponseWithHeaders(RestResponse.Success(null), response.headers)
        } else {
            ResponseWithHeaders(RestResponse.Success(response.body()), response.headers)
        }
    } catch (e: Throwable) {
        // There might be some corner cases that aren't entirely client failures
        ResponseWithHeaders(RestResponse.Failure.Client(e), response.headers)
    }
}

/*
 * Generic exceptions required for multiplatform, unless we typealias for each platform.
 *
 * Lots of returns are necessary for different paths, for which there's not a good solution.
 */
@Suppress("TooGenericExceptionCaught", "ReturnCount")
private suspend inline fun <reified T> HttpClient.getHelperNullable(
    authentication: Authentication,
    urlString: String
): ResponseWithHeaders<T?> {
    return requestSafelyNullable {
        request(urlString) {
            method = HttpMethod.Get
            configureAuth(authentication)
        }
    }
}

private fun HttpRequestBuilder.configureAuth(authentication: Authentication) {
    when (authentication) {
        is Authentication.Basic -> {
            headers {
                append(
                    HttpHeaders.Authorization,
                    BasicAuthHeader.generateValue(authentication.email.emailString, authentication.password)
                )
            }
        }
        is Authentication.Form -> {
            throw IllegalArgumentException("Form authentication is only supported by the session endpoint")
        }
        is Authentication.Bearer -> {
            headers { append(HttpHeaders.Authorization, BearerAuthHeader.generateValue(authentication.token)) }
        }
        Authentication.None -> {
            // Do nothing
        }
    }
}

@Suppress("TooGenericExceptionCaught")
suspend fun HttpResponse.toServerError(): ServerError {
    val serverError: ServerError =
        try {
            body()
        } catch (e: Throwable) {
            if (status == HttpStatusCode.Unauthorized) {
                ServerError(
                    CommonErrorCodes.UNAUTHORIZED,
                    ""
                )
            } else {
                ServerError(
                    CommonErrorCodes.PROXY,
                    "Failed decoding error from server; original message is ${e.message}"
                )
            }
        }

    return serverError
}
